'use client';

import InputAdornment from '@mui/material/InputAdornment';
import type { SxProps, Theme } from '@mui/material/styles';
import type React from 'react';
import { forwardRef, useState } from 'react';
import { GlassTextField } from '../GlassTextField';

const SUFFIX_MULTIPLIERS: Record<string, number> = { k: 1e3, m: 1e6, b: 1e9 };

/**
 * Parse a user-typed money string into a canonical numeric string.
 *
 *   "1500000"     → "1500000"
 *   "1,500,000"   → "1500000"
 *   "0.50"        → "0.5"
 *   "1.5m"        → "1500000"
 *   "250k"        → "250000"
 *   "1.2b"        → "1200000000"
 *   ""            → ""
 *
 * Decimals are preserved for plain digit input so callers that need cents
 * (discounts, payouts, fees) get correct values; K/M/B suffix amounts are
 * still rounded because the multiplier reliably produces an integer for
 * realistic shortcuts and avoids float-precision tails like
 * `1.23456789m → 1234567.89000001`. Suffix shortcuts are case-insensitive,
 * whitespace is tolerated, and anything un-parseable returns "" so the
 * input clears rather than persisting garbage.
 */
export function parseMoneyInput(input: string): string {
  const trimmed = input.trim().toLowerCase().replace(/,/g, '');
  if (!trimmed) return '';
  // Money fields don't accept negatives; reject leading minus or accounting
  // parens ("($100)") instead of silently coercing them to positives via
  // the non-digit strip below.
  if (/^[-(]/.test(trimmed)) return '';
  const suffix = trimmed.match(/^([\d.]+)\s*([kmb])$/);
  if (suffix?.[1] && suffix[2]) {
    const n = parseFloat(suffix[1]);
    const mult = SUFFIX_MULTIPLIERS[suffix[2]] ?? 1;
    return Number.isFinite(n) ? String(Math.round(n * mult)) : '';
  }
  const cleaned = trimmed.replace(/[^\d.]/g, '');
  if (!cleaned) return '';
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? String(n) : '';
}

/** Render a digit string with thousands separators (`1500000` → `1,500,000`). */
export function formatThousands(raw: string): string {
  if (!raw) return '';
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n.toLocaleString('en-US') : '';
}

export interface MoneyFieldProps {
  /**
   * Floating label. Optional so this works in min/max range layouts where
   * the label sits outside the input (e.g., "Min"/"Max" headers above two
   * money fields). When omitted, set `placeholder` to give the input a
   * recognizable empty state.
   */
  label?: string;
  /**
   * Canonical value with NO commas or suffix. Accepts both strings
   * (`"1500000"`) and numbers (`1500000`) for ergonomic adoption — call
   * sites that store amounts in numeric form don't have to coerce first.
   * Empty string or `null` / `undefined` renders as blank.
   */
  value: string | number | null | undefined;
  /** Receives the canonical digit string after the user finishes editing. */
  onChange: (next: string) => void;
  /** Currency symbol shown as the start adornment. Defaults to `$`. */
  currencySymbol?: string;
  /** Placeholder shown when value is empty. Useful when `label` is omitted. */
  placeholder?: string;
  /** Compact density (matches GlassTextField size="small"). */
  size?: 'small' | 'medium';
  /** Disable the input. */
  disabled?: boolean;
  fullWidth?: boolean;
  /** Pass-through sx for layout (flex, spacing) — forwarded to GlassTextField. */
  sx?: SxProps<Theme>;
  /** Validation error state — renders the field in error color. */
  error?: boolean;
  /** Helper / error message under the field. */
  helperText?: React.ReactNode;
  /** Required marker on the label. */
  required?: boolean;
  /** Pass-through name attribute for form integration. */
  name?: string;
  /** Focus the input on mount (for inline-edit pencils, dialogs). */
  autoFocus?: boolean;
}

function normalizeValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  return value;
}

/**
 * Money input that solves the long-zeros UX problem for principal amounts:
 *
 *   - Live display uses thousands separators (`1,500,000`)
 *   - On blur, K/M/B shortcuts expand (`1.5m` → `1,500,000`)
 *   - On focus, commas are stripped so the user edits raw digits
 *   - The parent's value stays as canonical digits (string), so existing
 *     parseFloat consumers keep working without changes
 *   - `value` accepts string | number for less ceremony at call sites
 */
export const MoneyField = forwardRef<HTMLInputElement, MoneyFieldProps>(function MoneyField(
  {
    label,
    value,
    onChange,
    currencySymbol = '$',
    placeholder,
    size,
    disabled,
    fullWidth = true,
    sx,
    error,
    helperText,
    required,
    name,
    autoFocus,
  },
  ref,
) {
  const normalized = normalizeValue(value);
  // While the user is editing, `draft` holds raw keystrokes; otherwise it's
  // null and the displayed value is derived from the parent's prop. This
  // single-state-source avoids the post-blur race where a useEffect could
  // overwrite the freshly-set display with stale prop data.
  const [draft, setDraft] = useState<string | null>(null);
  const display = draft ?? formatThousands(normalized);

  return (
    <GlassTextField
      inputRef={ref}
      fullWidth={fullWidth}
      disabled={disabled}
      label={label}
      placeholder={placeholder}
      size={size}
      sx={sx}
      error={error}
      helperText={helperText}
      required={required}
      name={name}
      autoFocus={autoFocus}
      value={display}
      onFocus={() => setDraft(normalized)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => {
        const raw = parseMoneyInput(e.target.value);
        onChange(raw);
        setDraft(null);
      }}
      slotProps={{ input: { startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment> } }}
      inputMode="decimal"
    />
  );
});

export default MoneyField;
